import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { AppBar, Avatar, Box, Button, Popover, Toolbar, Typography } from '@mui/material';
import { Add, ArrowBackIosNew } from '@mui/icons-material';

import { SIDEBAR_WIDTH } from '@/styles/constant';
import { deviceAddOpenAtom } from '../devices/DeviceAdd';
import { logoutAtom, tokenAtom } from '@/interfaces/auth';
import { userInfoAtom } from '@/interfaces/member';
import NotificationButton from '@/components/common/NotificationButton';
import PopupPasswordChange, { passwordPopupAtom } from './PopupPasswordChange';
import { Link } from 'react-router-dom';

interface TopBarProps {
  containerMode?: boolean;
}

// TODO: Get avatar from API
const avatarUrl = '/images/common/avatar-demo.png';

export default function TopBar({ containerMode }: TopBarProps) {
  const { data: userInfo } = useAtomValue(userInfoAtom);
  const { mutate: logout, data: isSuccess } = useAtomValue(logoutAtom);
  const setPasswordPopupShow = useSetAtom(passwordPopupAtom);
  const setDeviceAddShow = useSetAtom(deviceAddOpenAtom);
  const setToken = useSetAtom(tokenAtom);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  const open = Boolean(anchorEl);
  const id = open ? 'popover' : undefined;

  useEffect(() => {
    if (isSuccess === undefined) return;
    if (isSuccess) {
      setToken(RESET);
    } else {
      t('Common.message.serverErrorOccurred');
    }
  }, [isSuccess, setToken, t]);

  function openAddDevice() {
    setDeviceAddShow(true);
  }
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  function onLogoutClick() {
    logout();
  }

  return (
    <AppBar
      position="static"
      color="transparent"
      sx={{
        height: 75,
        boxShadow: 0,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Toolbar disableGutters sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          <img src={'/images/common/logo.png'} alt="logo" width={180} />
        </Box>
        {containerMode && (
          <Link to="/home">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNew />}
              sx={{
                height: 44,
                ml: 4,
                px: 4,
                borderRadius: 22,
              }}
            >
              <Typography fontWeight="medium">{t('Common.label.tempratureManagement')}</Typography>
            </Button>
          </Link>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {userInfo.isAdmin || containerMode ? null : (
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={openAddDevice}
            sx={{
              width: 200,
              height: 44,
              mr: 4,
              borderRadius: 22,
            }}
          >
            <Typography fontWeight="medium">{t('Common.label.addDevice')}</Typography>
          </Button>
        )}
        <NotificationButton />
        <Avatar src={avatarUrl} sx={{ mr: 4, cursor: 'pointer' }} onClick={handleClick} />
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          sx={{ mt: 2 }}
        >
          <Typography sx={{ p: 2, cursor: 'pointer' }} onClick={() => setPasswordPopupShow(true)}>
            {t('Common.label.changePassword')}
          </Typography>
          <Typography sx={{ p: 2, cursor: 'pointer' }} onClick={onLogoutClick}>
            {t('Common.label.logout')}
          </Typography>
        </Popover>
      </Toolbar>
      <PopupPasswordChange />
    </AppBar>
  );
}
